import React from "react";
import { Box, Button, Text, TextInput, UnstyledButton } from "@mantine/core";
import { IconChevronDown, IconInfoCircle } from "@tabler/icons-react";
import { CalendarEventType } from "../../../../../types/calendar";
import type { Employee, SalonService } from "../../../../../types/salon";
import type { GetTransactionsResponse } from "../../../../../types/transactions";
import { isStaffQualified } from "../../booking/guards";
import { serviceLength, suggestionQuery } from "../../booking/draftModel";
import { useSuggestedTimes } from "../../booking/useSuggestedTimes";
import { initialsOf } from "../grid/geometry";
import { staffPhotoOf } from "../grid/StaffFace";
import {
  conflictNoteText,
  findStaffConflict,
  MAX_SUGGESTIONS,
  slotLabel,
  slotParts,
  toMinutes,
} from "./detailModel";
import { MobileProviderPicker, ProviderOption } from "./MobileProviderPicker";
import { useMobileReschedule } from "./useMobileReschedule";
import { MobileDateField } from "../sheets/MobileDateField";
import styles from "./MobileDetailSheet.module.css";

export interface MobileRescheduleSectionProps {
  transaction: GetTransactionsResponse;
  bookedServiceId: string;
  timezone: string;
  services: SalonService[];
  /** Bookable active staff (the picker narrows to the qualified ones). */
  staff: Employee[];
  /** Calendar events for the conflict note (all staff). */
  events: CalendarEventType[];
  pickerOpen: boolean;
  onPickerOpenChange: (open: boolean) => void;
  onSaved: (day: string, transactionIds: string[]) => void;
}

const nameOf = (e: Employee): string =>
  `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim();

/**
 * Reschedule, inside the detail sheet (README screen 6): Date and Start time
 * (browser-native inputs), Provider (full-cover picker, qualified staff
 * only), suggested times, a conflict note naming the provider, Save changes.
 */
export const MobileRescheduleSection: React.FC<
  MobileRescheduleSectionProps
> = ({
  transaction,
  bookedServiceId,
  timezone,
  services,
  staff,
  events,
  pickerOpen,
  onPickerOpenChange,
  onSaved,
}) => {
  const r = useMobileReschedule({
    transaction,
    bookedServiceId,
    timezone,
    services,
    onSaved,
  });
  const row = r.activeRow;
  const svc = row
    ? services.find((s) => s.id === row.salonServiceId)
    : undefined;

  // Reschedule moves the tapped service only: its own suggested starts
  // (its provider, its current time as the requested start).
  const sq = suggestionQuery(row ? [row] : []);
  const { suggestions } = useSuggestedTimes({
    serviceIds: sq.serviceIds,
    staffIds: sq.staffIds,
    startTime: sq.startTime,
    date: r.day,
    timezone,
  });

  const startMin = row ? toMinutes(row.time) : 0;
  const endMin = row ? startMin + serviceLength(row) : 0;
  const txnId = transaction.transaction.id;

  const busyFor = (staffId: string) =>
    !!findStaffConflict(events, {
      staffId,
      day: r.day,
      startMin,
      endMin,
      excludeTransactionId: txnId,
    });

  const providerOptions: ProviderOption[] = staff
    .filter((e) => isStaffQualified(svc, e.id))
    .map((e) => ({
      id: e.id,
      name: nameOf(e),
      initials: initialsOf(e.first_name, e.last_name),
      photo: staffPhotoOf(e),
    }));

  const provider = staff.find((e) => e.id === row?.employeeId);
  const providerName = provider ? nameOf(provider) : "";
  const clash = !!row && !!row.employeeId && busyFor(row.employeeId);

  const shown = suggestions.slice(0, MAX_SUGGESTIONS);
  const isOn = (s: (typeof shown)[number]) => {
    const p = slotParts(s, timezone);
    return p.day === r.day && p.time === row?.time;
  };

  const handleSave = () => {
    if (clash) return;
    void r.save();
  };

  return (
    <Box className={styles.rsStack} data-testid="mobile-reschedule-section">
      <Box>
        <Text component="label" className={styles.fieldLabel}>
          Date
        </Text>
        <MobileDateField
          label="Date"
          value={r.day}
          onChange={r.setDate}
          className={styles.field}
        />
      </Box>
      <TextInput
        type="time"
        label="Start time"
        step={900}
        value={row?.time || ""}
        onChange={(e) => r.setTime(e.currentTarget.value)}
        classNames={{ label: styles.fieldLabel, input: styles.field }}
      />
      <Box>
        <Text component="label" className={styles.fieldLabel}>
          Provider
        </Text>
        <UnstyledButton
          className={styles.trigger}
          onClick={() => onPickerOpenChange(true)}
          aria-label={`Provider: ${providerName}`}
        >
          <Box component="span" className={styles.triggerText}>
            {providerName}
          </Box>
          <Box component="span" className={styles.triggerChev}>
            <IconChevronDown size={16} stroke={1.8} />
          </Box>
        </UnstyledButton>
      </Box>

      {shown.length > 0 && (
        <Box className={styles.sugWrap}>
          <Box className={styles.sugHead}>
            <Text component="span" className={styles.microLabel}>
              Suggested times
            </Text>
          </Box>
          <Box className={styles.sugList}>
            {shown.map((s) => (
              <UnstyledButton
                key={s.start_dt}
                className={styles.sugChip}
                data-on={isOn(s)}
                onClick={() => r.applySuggestion(s)}
              >
                {slotLabel(s, timezone)}
              </UnstyledButton>
            ))}
          </Box>
        </Box>
      )}

      {clash && (
        <Box className={styles.warnNote} role="alert">
          <IconInfoCircle size={15} stroke={1.8} />
          <Text component="span" className={styles.warnText}>
            {conflictNoteText(providerName)}
          </Text>
        </Box>
      )}

      <Button
        className={styles.primaryBtn}
        onClick={handleSave}
        loading={r.saving}
      >
        Save changes
      </Button>

      {pickerOpen && row && (
        <MobileProviderPicker
          options={providerOptions}
          selectedId={row.employeeId}
          onPick={(id) => {
            r.setProvider(id);
            onPickerOpenChange(false);
          }}
          onClose={() => onPickerOpenChange(false)}
        />
      )}
    </Box>
  );
};
